/**
 * Superadmin READ-ONLY consultation — the builder side of the handoff (mirrors the dashboard's
 * `view-handoff.ts`).
 *
 * The consulted dashboard opens the builder with the consultation token in the URL FRAGMENT
 * (`#view=<jwt>`) — a fragment is never sent to a server nor written to an access log. This runs
 * BEFORE React renders (called from main.tsx), so by the time `BuilderBoot` runs the borrowed token
 * is already in memory and NO cookie refresh is attempted. That ordering is the security point: the
 * old behaviour ("builder always authenticates from the shared refresh cookie") is exactly why the
 * dashboard used to BLOCK the builder in consultation — a stale seller cookie in the admin's browser
 * would have granted a full WRITE session. With the token seeded first, the cookie is never asked.
 *
 * Only a token that DECLARES itself `view_mode=read_only` is accepted through this door — a plain
 * write token in a fragment is not a consultation and must not be trusted in. The claim is read
 * unverified, which is fine: it never authorises anything. The server is the only gate —
 * `AdminStoreViewReadOnlyFilter` refuses every non-GET carried by this token regardless of what the
 * browser believes. The fragment is stripped immediately so a reload or copied URL cannot replay it.
 */

import { getToken, setToken } from './session';

const FRAGMENT_KEY = 'view';

/**
 * STICKY consultation latch. `isReadOnlyView()` used to be derived from the in-memory token alone —
 * but the token is CLEARED on a 401 (expiry), and a tab that stops being "read-only" falls back to
 * the normal auth path, where the shared refresh cookie could mint a full WRITE session (the same
 * cookie escape `withAuthRetry` closes for the in-flight request). A tab opened as a consultation
 * stays a consultation for its whole life: the latch never resets.
 */
let readOnlySession = false;

/** The `view_mode` claim of a JWT, unverified — `'read_only'` or null. */
function viewModeOf(token: string | null): 'read_only' | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const raw = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const padded = raw + '='.repeat((4 - (raw.length % 4)) % 4);
    const claims = JSON.parse(atob(padded)) as { view_mode?: unknown };
    return claims.view_mode === 'read_only' ? 'read_only' : null;
  } catch {
    return null;
  }
}

/** Seed a read-only consultation session from `#view=<jwt>` if the tab was opened that way. */
export function consumeViewHandoff(): void {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash;
  if (!hash || !hash.includes(FRAGMENT_KEY)) return;

  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const token = params.get(FRAGMENT_KEY);
  stripFragment();
  if (!token || viewModeOf(token) !== 'read_only') return;
  readOnlySession = true;
  setToken(token);
}

/** True for the whole life of a tab opened as a read-only admin consultation (banner + save gating). */
export function isReadOnlyView(): boolean {
  return readOnlySession || viewModeOf(getToken()) === 'read_only';
}

function stripFragment(): void {
  const { pathname, search } = window.location;
  window.history.replaceState(null, '', pathname + search);
}
